import { urlCarriesBearerCredential } from "@shared/security/bearer-urls";
import { navigateToDocument, reloadDocument } from "@/lib/document-navigation";
import { isEmbedDocument } from "@/lib/embed/is-embed-document";
import { resolveTelemetryEnvironment } from "@/lib/telemetry-runtime";

let isLive = false;

function navigationApi(): Navigation | null {
  return typeof window !== "undefined" && "navigation" in window
    ? window.navigation
    : null;
}

function parseUrl(raw: string, base?: string): URL | null {
  try {
    return new URL(raw, base);
  } catch {
    return null;
  }
}

function carriesCredential(raw: string | null, base: string): boolean {
  const url = raw === null ? null : parseUrl(raw, base);
  return url === null || urlCarriesBearerCredential(url);
}

function referrerCarriesCredential(base: string): boolean {
  const referringPage = document.referrer === "" ? null : parseUrl(document.referrer);
  return (
    referringPage !== null &&
    referringPage.origin === new URL(base).origin &&
    urlCarriesBearerCredential(referringPage)
  );
}

function documentHistoryIsClean(navigation: Navigation, base: string): boolean {
  const entries = navigation.entries().filter((entry) => entry.sameDocument);
  return entries.length > 0 && entries.every((entry) => !carriesCredential(entry.url, base));
}

function isMeasurable(navigation: Navigation): boolean {
  const base = window.location.href;
  return (
    resolveTelemetryEnvironment() === "production" &&
    !isEmbedDocument(window.location.pathname) &&
    !referrerCarriesCredential(base) &&
    documentHistoryIsClean(navigation, base)
  );
}

function withoutFragment(url: URL): string {
  return url.href.slice(0, url.href.length - url.hash.length);
}

function loadCredentialUrlsAsDocuments(event: NavigateEvent): void {
  if (!event.destination.sameDocument) return;
  if (!carriesCredential(event.destination.url, window.location.href)) return;
  if (!event.cancelable) {
    event.currentTarget?.addEventListener("currententrychange", reloadDocument, { once: true });
    return;
  }
  event.preventDefault();
  const target = new URL(event.destination.url);
  if (withoutFragment(target) === withoutFragment(new URL(window.location.href))) return;
  navigateToDocument(target.href);
}

export function subscribeToTagManagerDocument(onChange: () => void): () => void {
  const navigation = navigationApi();
  if (navigation === null) return () => {};
  navigation.addEventListener("currententrychange", onChange);
  return () => navigation.removeEventListener("currententrychange", onChange);
}

export function isTagManagerDocument(): boolean {
  if (isLive) return true;
  const navigation = navigationApi();
  if (navigation === null || !isMeasurable(navigation)) return false;
  navigation.addEventListener("navigate", loadCredentialUrlsAsDocuments);
  isLive = true;
  return true;
}
